import React from 'react';
import styled, { css } from 'styled-components';

import { CURSOR_INTERACTIVE } from '../../styles/cursors';

import { media } from '../../styles/breakpoints';
import { email as siteEmail, businessInfo, legalLabels } from '../../utils/generalData';

export const PageWrapper = styled.div`
    max-width: 1200px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    position: relative;
`;

export const Section = styled.section`
    padding: 60px 0;
    border-bottom: 2px solid var(--grid-color);

    &:last-child {
        border-bottom: none;
    }

    ${media.md} {
        padding: 40px 0;
    }

    ${media.sm} {
        padding: 30px 0;
    }

    ${media.xs} {
        padding: 20px 0;
    }
`;

export const SectionTitle = styled.h2`
    font-size: 32px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    position: relative;
    display: inline-block;
    margin-bottom: 40px;
    color: var(--primary-color);

    &:after {
        content: "//";
        color: var(--text-color);
        opacity: 0.5;
        margin-left: 15px;
    }

    ${media.md} {
        font-size: 28px;
        margin-bottom: 30px;
        letter-spacing: 1.5px;
    }

    ${media.sm} {
        font-size: 24px;
        margin-bottom: 25px;
        letter-spacing: 1px;

        &:after {
            margin-left: 10px;
        }
    }

    ${media.xs} {
        font-size: 20px;
        margin-bottom: 20px;

        &:after {
            content: "/";  // Shorter decoration on tiny screens
        }
    }
`;

export const SectionHeader = styled.div`
    margin-bottom: 60px;
    position: relative;
    text-align: center;

    ${media.lg} {
        margin-bottom: 50px;
    }

    ${media.md} {
        margin-bottom: 40px;
    }

    ${media.sm} {
        margin-bottom: 30px;
    }

    ${media.xs} {
        margin-bottom: 25px;
    }
`;

export const SubHeadline = styled.p`
    font-size: 18px;
    max-width: 700px;
    margin: 0 auto 40px;
    color: var(--text-muted);
    line-height: 1.6;

    ${media.lg} {
        max-width: 90%;
    }

    ${media.md} {
        font-size: 16px;
        margin-bottom: 30px;
        line-height: 1.5;
    }

    ${media.sm} {
        font-size: 15px;
        margin-bottom: 25px;
    }

    ${media.xs} {
        font-size: 14px;
        margin-bottom: 20px;
        line-height: 1.4;
    }
`;

export const Placeholder = styled.div`
    border: 1px dashed var(--primary-color);
    background-color: rgba(255, 51, 153, 0.05);
    min-height: 250px;
    display: flex;
    flex-direction: column; /* Allow text/img inside */
    align-items: center;
    justify-content: center;
    color: var(--primary-color);
    font-size: 14px;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin: 30px auto;
    max-width: 800px;
    width: 100%;
    text-align: center;
    padding: 20px;

    ${media.md} {
        min-height: 180px;
        font-size: 12px;
        margin: 20px auto;
    }
`;

export const Highlight = styled.span`
    color: var(--primary-color);
    font-weight: 700;
`;

// Add other common elements like USPSection etc. if desired
export const USPSection = styled(Section)`
    background-color: rgba(255, 51, 153, 0.05);
    border: 1px solid var(--primary-color);
    margin-top: 40px;
    padding: 40px;

    /* Ensure bottom border persists even if it's the last section */
    &:last-child {
        border-bottom: 1px solid var(--primary-color);
    }
`;

export const USPTitle = styled(SectionTitle)`
    font-size: 28px;
    margin-bottom: 20px;
`;

export const USPDescription = styled.p`
    font-size: 16px;
    line-height: 1.7;
    color: #cccccc;
    margin-bottom: 20px;
    max-width: 900px;
`;


export const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 40px;
    margin-top: 30px;

    ${media.md} {
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 30px;
    }

    ${media.sm} {
        grid-template-columns: 1fr;
        gap: 25px;
    }
`;


// Make feature items more responsive
export const FeatureItem = styled.div`
    border: 1px solid var(--grid-color);
    background-color: rgba(0, 0, 0, 0.3);
    padding: 25px;
    transition: border-color 0.3s ease, transform 0.3s ease;

    &:hover {
        border-color: var(--primary-color);
        transform: translateY(-5px);
    }

    ${media.md} {
        padding: 20px;
    }

    ${media.sm} {
        padding: 15px;

        &:hover {
            transform: translateY(-3px); // Less dramatic hover on mobile
        }
    }
`;
export const FeatureTitle = styled.h3`
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 15px;
    text-transform: uppercase;
    letter-spacing: 1px;
`;


export const FeatureDescription = styled.p`
    font-size: 14px;
    color: var(--text-muted);
    line-height: 1.6;
`;

export const HeroSection = styled(Section)`
    text-align: center;
    border-bottom: none; /* No border after hero */
    padding-top: 0; /* Less padding at the top */
`;

export const PageTitle = styled.h1`
    font-size: 48px; /* Slightly smaller than main hero */
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 20px;
    color: var(--text-color);
    text-shadow: 0 0 8px rgba(255, 51, 153, 0.4);
`;
// Technical Specs List Style
export const TechSpecsList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin-top: 20px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto; /* Center the list */
    
    ${media.xs} {
        font-size: 13px; // Slightly smaller text on tiny screens
    }
`;

export const TechSpecItem = styled.li`
  margin-bottom: 12px;
  font-size: 14px;
  color: var(--text-muted);
  display: flex;
  justify-content: space-between;
  border-bottom: 1px dashed var(--grid-color);
  padding-bottom: 8px;

  span:first-child {
    color: var(--text-color);
    font-weight: bold;
    margin-right: 15px;
  }
  span:last-child {
    color: var(--primary-color);
    text-align: right;
  }
`;

// Simple paragraph for tribute
export const TributeText = styled.p`
    font-size: 14px;
    color: #888888;
    margin-top: 30px;
    font-style: italic;
`;


export const TestimonialSection = styled(Section)`
    background-color: rgba(255, 51, 153, 0.05);
    border-top: 1px solid var(--primary-color);
    border-bottom: 1px solid var(--primary-color);
    margin-top: 40px;
    padding: 40px;

    ${media.md} {
        padding: 30px 20px;
        margin-top: 30px;
    }
`;

export const TestimonialBlock = styled.blockquote`
    border-left: 3px solid var(--primary-color);
    margin: 30px auto;
    padding: 15px 25px;
    font-style: italic;
    color: #cccccc;
    max-width: 600px;
    position: relative;

    p {
        margin-bottom: 10px;
        font-size: 16px;
    }

    footer {
        font-style: normal;
        font-size: 13px;
        color: #888888;
        text-align: right;

        &:before {
            content: "— ";
        }
    }

    ${media.md} {
        margin: 25px auto;
        padding: 12px 20px;

        p {
            font-size: 15px;
        }
    }

    ${media.sm} {
        margin: 20px auto;
        padding: 10px 15px;
        border-left-width: 2px;

        p {
            font-size: 14px;
        }

        footer {
            font-size: 12px;
        }
    }

    ${media.xs} {
        margin: 15px auto;
        padding: 8px 12px;
    }
`;
export const FigureWrapper = styled.figure`
    margin: 30px auto;
    max-width: 800px;
    width: 100%;
    text-align: center;

    ${media.lg} {
        max-width: 90%; // Slightly narrower on smaller screens
    }

    ${media.md} {
        margin: 25px auto;
    }

    ${media.sm} {
        margin: 20px auto;
    }

    ${media.xs} {
        margin: 15px auto;
    }
`;
export const FigureImage = styled.img`
    display: block;
    max-width: 100%;
    width: 100%;
    height: auto;
    margin: 0 auto 10px auto;
    border: 1px solid var(--grid-color);
    background-color: rgba(255, 51, 153, 0.05);

    ${media.sm} {
        border-width: 1px; // Thinner border on mobile
        margin-bottom: 8px;
    }
`;

export const FigureCaption = styled.figcaption`
    margin-top: 10px;
    font-size: 14px;
    color: var(--text-muted);
    font-style: italic;
    line-height: 1.5;
    padding: 0 10px;

    ${media.md} {
        font-size: 13px;
    }

    ${media.sm} {
        font-size: 12px;
        padding: 0 5px;
        margin-top: 8px;
    }
`;

export const VideoElement = styled.video`
    display: block;
    max-width: 100%;
    width: 100%;
    height: auto;
    margin: 0 auto 10px auto;
    border: 1px solid var(--grid-color);
    background-color: rgba(0, 0, 0, 0.5);

    ${media.sm} {
        border-width: 1px;
    }
`;

export const ProgressBarContainer = styled.div`
    flex-grow: 1;
    height: 6px;
    cursor: ${CURSOR_INTERACTIVE};
    background-color: rgba(255, 51, 153, 0.15);
    border: 1px solid var(--grid-color);
    position: relative; /* For positioning the fill */
    overflow: hidden; /* Hide overflow of fill */
`;

export const ProgressBarFill = styled.div`
    height: 100%;
    background-color: var(--primary-color);
    cursor: ${CURSOR_INTERACTIVE};
    width: ${props => props.progress || 0}%;
    transition: width 0.1s linear; /* Smooth progress update */
    position: absolute;
    top: 0;
    left: 0;
`;

export const AudioPlayerWrapper = styled.div`
    border: 1px solid var(--grid-color);
    background-color: rgba(0, 0, 0, 0.5);
    padding: 20px;
    max-width: 700px;
    margin: 30px auto;
    font-family: inherit;

    ${media.md} {
        padding: 15px;
        margin: 25px auto;
    }

    ${media.sm} {
        padding: 12px;
        margin: 20px auto;
    }

    ${media.xs} {
        padding: 10px;
        margin: 15px auto;
    }
`;

export const TrackList = styled.div`
    list-style: none;
    padding: 0;
    margin: 0 0 20px 0;
    max-height: 150px;
    overflow-y: auto;
    overflow-x: hidden; /* Ensure no horizontal scrollbar */
`;

// Define styles for active/playing state (does not need export itself)
const activeTrackStyles = css`
    background-color: rgba(255, 51, 153, 0.2);
    color: var(--primary-color);
    font-weight: bold; // Keep bold
    border-left: 3px solid var(--primary-color); // Add left border highlight

    &:before {
        content: "▶";
        color: var(--primary-color);
        margin-right: 10px; // Keep space
        font-size: 10px;
        min-width: 10px; // Ensure space is reserved
        text-align: center;
    }
`;

// Define TrackArtistSpan FIRST so we can reference it in TrackItem
export const TrackArtistSpan = styled.span`
    grid-area: artist;
    font-size: 14px; /* Changed from 12px to 14px as requested */
    color: #888;
    font-style: italic;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    transition: color 0.3s ease;

    a {
        color: inherit;
        text-decoration: underline;
        text-decoration-color: transparent;
        text-underline-offset: 3px;
        position: relative;
        transition: color 0.3s ease, text-decoration-color 0.3s ease;

        &:hover {
            color: var(--primary-color);
            text-decoration-color: var(--primary-color);
        }
    }
`;

export const TrackTitleSpan = styled.span`
    grid-area: title;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;
// Now define TrackItem AFTER the spans
export const TrackItem = styled.div`
    position: relative;
    margin-bottom: 0;
    border-bottom: 1px solid var(--grid-color);
    border-left: 3px solid transparent;
    width: 100%;
    min-height: 40px;
    background-color: transparent;

    /* Transitions for whole tile */
    transition: background-color 0.3s ease, border-color 0.3s ease;

    /* Create layout */
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-areas: "icon title artist";
    align-items: center;
    gap: 10px;
    padding: 8px 12px;

    /* Hover state for whole tile */
    &:has(.track-play-button:hover) {
        background-color: rgba(255, 51, 153, 0.1);
        border-left-color: var(--primary-color);
        color: var(--text-color);
    }

    /* Focus state for whole tile - when button is focused */
    &:has(.track-play-button:focus-visible) {
        background-color: rgba(255, 51, 153, 0.1);
        border-left-color: var(--primary-color);
        color: var(--text-color);
        box-shadow: inset 0 0 0 2px var(--primary-color);
    }

    /* Track play button */
    .track-play-button {
        /* Reset button styles */
        all: unset;
        box-sizing: border-box;

        /* Layout - spans icon + title */
        grid-column: 1 / 3;
        grid-row: 1;

        /* Internal grid */
        display: grid;
        grid-template-columns: subgrid;
        grid-template-areas: "icon title";
        align-items: center;

        /* Styling */
        color: var(--text-muted);
        font-size: 14px;
        font-family: inherit;
        cursor: ${CURSOR_INTERACTIVE};

        /* Icon */
        .track-icon {
            grid-area: icon;
            width: 10px;
            display: block;
        }

        /* Title */
        ${TrackTitleSpan} {
            grid-area: title;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        /* Artist (when no link) */
        ${TrackArtistSpan} {
            grid-column: 3;
            grid-area: artist;
        }

        /* Focus states */
        &:focus {
            outline: none;
        }

        &:focus-visible {
            outline: none;
        }
    }

    /* Artist link - separate column */
    .track-artist-link {
        grid-column: 3;
        grid-row: 1;
        grid-area: artist;

        /* Styling */
        color: #888;
        font-size: 14px;
        font-style: italic;
        text-decoration: none;
        cursor: ${CURSOR_INTERACTIVE};

        /* Layout */
        display: flex;
        align-items: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding: 0; /* No padding - borders will provide spacing */
        box-sizing: border-box; /* Prevent borders from increasing height */
        border-top: 2px solid transparent; /* Reserve space for focus border */
        border-bottom: 2px solid transparent; /* Reserve space for focus border */
        margin: -2px 0; /* Compensate for the transparent borders */

        /* Transitions */
        transition: color 0.3s ease, border-color 0.3s ease;

        /* Hover */
        &:hover {
            color: var(--primary-color);
        }

        /* Focus */
        &:focus {
            outline: none;
        }

        &:focus-visible {
            outline: none;
            /* Only horizontal borders - top and bottom, close to text */
            border-top-color: var(--primary-color);
            border-bottom-color: var(--primary-color);
        }
    }

    /* Last item - no bottom border */
    &:last-child {
        border-bottom: none;
    }

    /* Active state styling */
    ${({ $isActive }) => $isActive && css`
        background-color: rgba(255, 51, 153, 0.2);
        color: var(--primary-color);
        font-weight: bold;
        border-left: 3px solid var(--primary-color);

        .track-icon::before {
            content: "▶";
            color: var(--primary-color);
        }
    `}

    ${media.md} {
        grid-template-columns: auto 1fr;
        grid-template-rows: auto auto;
        grid-template-areas:
            "icon title"
            ".    artist";
        gap: 5px 10px;

        .track-play-button {
            grid-column: 1 / 3;
            grid-row: 1 / 2;
        }

        .track-artist-link {
            grid-column: 2;
            grid-row: 2;
        }
    }
`;

export const ControlsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 15px; // Keep gap
    border-top: 1px solid var(--grid-color);
    padding-top: 15px;
    width: 100%;
`;

export const PlayPauseButton = styled.button`
    background: transparent;
    border: 1px solid var(--primary-color);
    color: var(--primary-color);
    padding: 8px 10px;
    font-size: 18px;
    line-height: 1;
    cursor: ${CURSOR_INTERACTIVE};
    transition: background-color 0.2s ease, color 0.2s ease;
    min-width: 40px;
    text-align: center;
    flex-shrink: 0; // Prevent button from shrinking

    &:hover {
        background-color: var(--primary-color);
        color: var(--background-color);
        border-color: var(--primary-color);
    }

    /* Remove default focus outline */
    &:focus {
        outline: none;
    }

    /* Only show focus indicator on keyboard navigation */
    &:focus-visible {
        box-shadow: 0 0 0 2px var(--primary-color);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        &:hover {
            background-color: transparent;
            color: var(--primary-color);
        }
    }
`;

export const TrackDisplayContainer = styled.div`
    flex-grow: 1;       /* ADD/Ensure this */
    flex-shrink: 1;     /* ADD/Ensure this - allow shrinking */
    flex-basis: 0;        /* ADD/Ensure this - start calculation from 0 */
    min-width: 50px;     /* ADJUSTED - smaller min-width allows more shrinking */
    overflow: hidden; // Keep this for marquee
`;


export const TimeDisplay = styled.div`
    font-size: 13px;
    color: var(--primary-color);
    min-width: 85px; // Slightly reduced min-width just in case
    text-align: right;
    flex-shrink: 0; // Prevent time display from shrinking
`;

// Language toggle for bilingual pages
export const LanguageToggle = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 20px;
`;

export const LangButton = styled.button`
    background: transparent;
    color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-color)'};
    border: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--grid-color)'};
    padding: 6px 12px;
    font-size: 14px;
    font-weight: ${props => props.$active ? '700' : '600'};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--primary-color);
        color: var(--primary-color);
    }
`;

// InfoBlock for legal pages - contact info, addresses, etc.
export const InfoBlock = styled.div`
    background: rgba(255, 255, 255, 0.03);
    border-left: 3px solid var(--primary-color);
    padding: 20px;
    margin: 20px 0;

    p {
        margin-bottom: 8px;
        font-size: 16px;
        color: #cccccc;

        &:last-child {
            margin-bottom: 0;
        }
    }

    a {
        color: var(--secondary-color);
        text-decoration: underline;
        text-underline-offset: 3px;

        &:hover {
            color: var(--primary-color);
        }
    }

    strong {
        color: var(--text-color);
        font-weight: 700;
    }

    ${media.sm} {
        padding: 15px;
    }
`;

// Reusable contact block for legal pages
export const LegalContactBlock = ({ lang = 'en' }) => (
    <InfoBlock>
        <p><strong>{businessInfo.name}, {businessInfo.businessType[lang]}</strong></p>
        <p>{businessInfo.address.street}</p>
        <p>{businessInfo.address.city}, {businessInfo.address.country[lang]}</p>
        <p><strong>{legalLabels[lang].siret}</strong> {businessInfo.siret}</p>
        <p><strong>{legalLabels[lang].email}</strong> <a href={`mailto:${siteEmail}`}>{siteEmail}</a></p>
        <p><strong>{legalLabels[lang].phone}</strong> {businessInfo.phone}</p>
    </InfoBlock>
);

// Compact contact block (without SIRET, for contact sections)
export const ContactBlock = ({ lang = 'en' }) => (
    <InfoBlock>
        <p><strong>{businessInfo.name}, {businessInfo.businessType[lang]}</strong></p>
        <p>{businessInfo.address.street}</p>
        <p>{businessInfo.address.city}, {businessInfo.address.country[lang]}</p>
        <p><strong>{legalLabels[lang].email}</strong> <a href={`mailto:${siteEmail}`}>{siteEmail}</a></p>
        <p><strong>{legalLabels[lang].phone}</strong> {businessInfo.phone}</p>
    </InfoBlock>
);

